import React, { useMemo } from 'react';
import './TaskDeadlinesGantt.css';

export interface TaskDeadlineGanttExtraBar {
  startMs: number | null;
  endMs: number | null;
  barOverdue?: boolean;
}

export interface TaskDeadlineGanttRow {
  key: string;
  startMs: number | null;
  endMs: number | null;
  isCurrent: boolean;
  isTotal: boolean;
  tier?: 'contract' | 'task';
  expenseClosingDateMs?: number | null;
  expenseClosingDateLabel?: string | null;
  expenseClosedOnTime?: boolean | null;
  totalMarkerMs?: number | null;
  barOverdue?: boolean;
  extraBars?: TaskDeadlineGanttExtraBar[];
  rowSpan?: number;
}

interface TaskDeadlinesGanttProps {
  rows: TaskDeadlineGanttRow[];
  spanStartMs: number;
  spanEndMs: number;
  axisStartLabel: string;
  axisEndLabel: string;
  totalOverdue: boolean | null;
  todayMs: number;
  referenceMarkerMs?: number | null;
  referenceMarkerLabel?: string;
  showGlobalReferenceMarker?: boolean;
  showTodayMarker?: boolean;
  showLegend?: boolean;
  alignGroupedHeader?: boolean;
  showTimelineHeader?: boolean;
  showStatusDesignations?: boolean;
  closingLegendLabel?: string;
  className?: string;
}

interface BarLayout {
  leftPct: number;
  widthPct: number;
  visible: boolean;
}

function layoutBar(
  startMs: number | null,
  endMs: number | null,
  spanStartMs: number,
  spanEndMs: number,
): BarLayout {
  if (startMs == null) {
    return { leftPct: 0, widthPct: 0, visible: false };
  }

  const span = Math.max(spanEndMs - spanStartMs, 86_400_000);
  const effectiveEnd = endMs ?? startMs;
  const leftPct = ((startMs - spanStartMs) / span) * 100;
  const rawWidth = ((effectiveEnd - startMs) / span) * 100;
  const widthPct = Math.max(rawWidth, endMs == null ? 4 : 10);

  return {
    leftPct: Math.min(Math.max(leftPct, 0), 100),
    widthPct: Math.min(widthPct, 100 - Math.max(leftPct, 0)),
    visible: true,
  };
}

function markerPct(ms: number, spanStartMs: number, spanEndMs: number): number {
  const span = Math.max(spanEndMs - spanStartMs, 86_400_000);
  return ((ms - spanStartMs) / span) * 100;
}

function planBarModifier(row: TaskDeadlineGanttRow): string {
  if (row.isTotal) return ' task-deadlines-gantt__bar--plan-total';
  if (row.tier === 'contract') return ' task-deadlines-gantt__bar--plan-contract';
  if (row.tier === 'task') return ' task-deadlines-gantt__bar--plan-task';
  return ' task-deadlines-gantt__bar--plan';
}

const MONTH_LABELS = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

interface TimelineYearBand {
  year: number;
  leftPct: number;
  widthPct: number;
}

interface TimelineMonthTick {
  leftPct: number;
  label: string;
}

const DAY_MS = 86_400_000;
/** Month ticks stay readable only on compact ranges; huge spans (e.g. year typed as 0002) must not walk millennia. */
const MAX_MONTH_TICKS = 60;
const MAX_YEAR_BANDS = 40;

function buildTimelineScale(spanStartMs: number, spanEndMs: number): {
  years: TimelineYearBand[];
  months: TimelineMonthTick[];
} {
  const years: TimelineYearBand[] = [];
  const months: TimelineMonthTick[] = [];
  if (!Number.isFinite(spanStartMs) || !Number.isFinite(spanEndMs)) {
    return { years, months };
  }

  const span = Math.max(spanEndMs - spanStartMs, DAY_MS);
  const start = new Date(spanStartMs);
  const end = new Date(spanEndMs);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return { years, months };
  }

  const startYear = start.getFullYear();
  const endYear = end.getFullYear();
  const yearCount = Math.max(endYear - startYear + 1, 1);

  const pushYearBand = (year: number, leftPct: number, rightPct: number): void => {
    const widthPct = Math.max(rightPct - leftPct, 0);
    if (widthPct > 0) {
      years.push({ year, leftPct, widthPct });
    }
  };

  if (yearCount <= MAX_YEAR_BANDS) {
    for (let year = startYear; year <= endYear; year += 1) {
      const yearStartMs = new Date(year, 0, 1).getTime();
      const yearEndMs = new Date(year + 1, 0, 1).getTime();
      const leftPct = Math.max(((yearStartMs - spanStartMs) / span) * 100, 0);
      const rightPct = Math.min(((yearEndMs - spanStartMs) / span) * 100, 100);
      pushYearBand(year, leftPct, rightPct);
    }
  } else {
    pushYearBand(startYear, 0, endYear === startYear ? 100 : 50);
    if (endYear !== startYear) {
      pushYearBand(endYear, 50, 100);
    }
  }

  const approxMonths = span / (DAY_MS * 30.4375);
  if (approxMonths <= MAX_MONTH_TICKS) {
    const cursor = new Date(spanStartMs);
    cursor.setDate(1);
    cursor.setHours(0, 0, 0, 0);
    let steps = 0;
    while (cursor.getTime() <= spanEndMs && steps < MAX_MONTH_TICKS + 2) {
      const ms = cursor.getTime();
      const leftPct = ((ms - spanStartMs) / span) * 100;
      if (leftPct > 100) break;
      if (leftPct >= 0) {
        months.push({
          leftPct,
          label: MONTH_LABELS[cursor.getMonth()],
        });
      }
      cursor.setMonth(cursor.getMonth() + 1);
      steps += 1;
    }
  }

  return { years, months };
}

const TaskDeadlinesGantt: React.FC<TaskDeadlinesGanttProps> = ({
  rows,
  spanStartMs,
  spanEndMs,
  axisStartLabel,
  axisEndLabel,
  totalOverdue,
  todayMs,
  referenceMarkerMs,
  referenceMarkerLabel = 'Сегодня',
  showGlobalReferenceMarker = false,
  showTodayMarker = false,
  showLegend = false,
  alignGroupedHeader = false,
  showTimelineHeader = false,
  showStatusDesignations = true,
  closingLegendLabel = 'закрытие по расходам',
  className,
}) => {
  const bars = useMemo(
    () => rows.map((row) => ({ row, bar: layoutBar(row.startMs, row.endMs, spanStartMs, spanEndMs) })),
    [rows, spanStartMs, spanEndMs],
  );

  const globalMarkerMs = referenceMarkerMs ?? todayMs;
  const globalMarkerPct = useMemo(() => {
    const pct = markerPct(globalMarkerMs, spanStartMs, spanEndMs);
    return pct >= 0 && pct <= 100 ? pct : null;
  }, [globalMarkerMs, spanStartMs, spanEndMs]);

  const todayMarkerPct = useMemo(() => {
    if (!showTodayMarker) return null;
    const pct = markerPct(todayMs, spanStartMs, spanEndMs);
    return pct >= 0 && pct <= 100 ? pct : null;
  }, [showTodayMarker, todayMs, spanStartMs, spanEndMs]);

  const timelineScale = useMemo(
    () => (showTimelineHeader ? buildTimelineScale(spanStartMs, spanEndMs) : null),
    [showTimelineHeader, spanStartMs, spanEndMs],
  );

  const usePlanBars = className?.includes('task-deadlines-gantt--contract-deadlines') ?? false;

  if (rows.length === 0) {
    return <div className="task-deadlines-gantt task-deadlines-gantt--empty">Нет данных для визуализации</div>;
  }

  return (
    <div
      className={`task-deadlines-gantt${alignGroupedHeader ? ' task-deadlines-gantt--aligned-header' : ''}${className ? ` ${className}` : ''}`}
      role="img"
      aria-label="Схема сроков по договорам"
    >
      {alignGroupedHeader ? <div className="task-deadlines-gantt__header-spacer" aria-hidden /> : null}
      {showTimelineHeader && timelineScale ? (
        <div className="task-deadlines-gantt__timeline" aria-hidden>
          <div className="task-deadlines-gantt__timeline-years">
            {timelineScale.years.map((band) => (
              <div
                key={`year-${band.year}-${band.leftPct}`}
                className="task-deadlines-gantt__timeline-year"
                style={{ left: `${band.leftPct}%`, width: `${band.widthPct}%` }}
              >
                {band.year}
              </div>
            ))}
          </div>
          <div className="task-deadlines-gantt__timeline-months">
            {timelineScale.months.map((tick, index) => (
              <div
                key={`month-${tick.leftPct}-${index}`}
                className="task-deadlines-gantt__timeline-month"
                style={{ left: `${tick.leftPct}%` }}
              >
                {tick.label}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="task-deadlines-gantt__axis" aria-hidden>
          <span>{axisStartLabel}</span>
          <span>{axisEndLabel}</span>
        </div>
      )}
      <div className="task-deadlines-gantt__body">
        {showTodayMarker && todayMarkerPct != null ? (
          <div
            className="task-deadlines-gantt__global-marker task-deadlines-gantt__global-marker--today"
            style={{ left: `${todayMarkerPct}%` }}
            title="Сегодня"
            aria-hidden
          />
        ) : null}
        {showGlobalReferenceMarker && globalMarkerPct != null ? (
          <div
            className="task-deadlines-gantt__global-marker"
            style={{ left: `${globalMarkerPct}%` }}
            title={referenceMarkerLabel}
            aria-hidden
          />
        ) : null}
        <div className="task-deadlines-gantt__rows">
          {bars.map(({ row, bar }) => {
            const isTaskTier = row.tier === 'task';
            const isContractTier = row.tier === 'contract';
            const barOverdue = row.isTotal ? totalOverdue === true : row.barOverdue === true;
            const hasClosing = row.expenseClosingDateMs != null;
            const closingMarkerPct =
              hasClosing ? markerPct(row.expenseClosingDateMs!, spanStartMs, spanEndMs) : null;
            const showClosingMarker =
              closingMarkerPct != null && closingMarkerPct >= 0 && closingMarkerPct <= 100;
            const planEndPct = row.endMs != null ? markerPct(row.endMs, spanStartMs, spanEndMs) : null;
            const referencePct = markerPct(globalMarkerMs, spanStartMs, spanEndMs);
            const rowSpan = Math.max(row.rowSpan ?? 1, 1);
            const extraLayouts = (row.extraBars ?? []).map((extra) =>
              layoutBar(extra.startMs, extra.endMs, spanStartMs, spanEndMs),
            );

            const showOverdueTailClosedLate =
              showStatusDesignations &&
              usePlanBars &&
              showClosingMarker &&
              planEndPct != null &&
              closingMarkerPct != null &&
              row.expenseClosedOnTime === false &&
              closingMarkerPct > planEndPct;

            const showOverdueTailOpen =
              showStatusDesignations &&
              usePlanBars &&
              !hasClosing &&
              barOverdue &&
              planEndPct != null &&
              referencePct > planEndPct;

            const overdueTailLeftPct = planEndPct ?? 0;
            const overdueTailWidthPct = showOverdueTailClosedLate
              ? closingMarkerPct! - overdueTailLeftPct
              : showOverdueTailOpen
                ? referencePct - overdueTailLeftPct
                : 0;

            const showRemainderTailClosedEarly =
              showStatusDesignations &&
              usePlanBars &&
              showClosingMarker &&
              planEndPct != null &&
              closingMarkerPct != null &&
              row.expenseClosedOnTime === true &&
              closingMarkerPct < planEndPct;

            const showRemainderTailOpen =
              showStatusDesignations &&
              usePlanBars &&
              planEndPct != null &&
              referencePct < planEndPct &&
              !barOverdue &&
              !hasClosing &&
              !showOverdueTailClosedLate;

            const showRemainderTail = showRemainderTailClosedEarly || showRemainderTailOpen;
            const remainderTailLeftPct = showRemainderTailClosedEarly
              ? closingMarkerPct!
              : referencePct;
            const remainderTailWidthPct = showRemainderTail
              ? planEndPct! - remainderTailLeftPct
              : 0;

            const barClass = usePlanBars
              ? `task-deadlines-gantt__bar${planBarModifier(row)}${isTaskTier ? ' task-deadlines-gantt__bar--thin' : ''}`
              : `task-deadlines-gantt__bar${row.isTotal ? ' task-deadlines-gantt__bar--total' : ''}${isContractTier ? ' task-deadlines-gantt__bar--contract' : ''}${isTaskTier ? ' task-deadlines-gantt__bar--thin' : ''}${barOverdue ? ' task-deadlines-gantt__bar--overdue' : ' task-deadlines-gantt__bar--on-time'}`;

            return (
              <div
                key={row.key}
                className={`task-deadlines-gantt__row${row.isTotal ? ' task-deadlines-gantt__row--total' : ''}${row.isCurrent ? ' task-deadlines-gantt__row--current' : ''}${isTaskTier ? ' task-deadlines-gantt__row--task' : ''}${isContractTier ? ' task-deadlines-gantt__row--contract' : ''}`}
                style={
                  rowSpan > 1
                    ? {
                        height: `${28 * rowSpan}px`,
                        minHeight: `${28 * rowSpan}px`,
                        alignItems: 'flex-start',
                        paddingTop: '7px',
                        boxSizing: 'border-box',
                      }
                    : undefined
                }
              >
                <div className="task-deadlines-gantt__lane">
                  {bar.visible ? (
                    <div
                      className={barClass}
                      style={{ left: `${bar.leftPct}%`, width: `${bar.widthPct}%` }}
                    />
                  ) : (
                    <div className={`${barClass} task-deadlines-gantt__bar--placeholder`} />
                  )}
                  {extraLayouts.map((extraBar, extraIndex) =>
                    extraBar.visible ? (
                      <div
                        key={`${row.key}-ext-${extraIndex}`}
                        className={`${barClass} task-deadlines-gantt__bar--extension`}
                        style={{ left: `${extraBar.leftPct}%`, width: `${extraBar.widthPct}%` }}
                        title="Продление ДС"
                      />
                    ) : null,
                  )}
                  {showRemainderTail ? (
                    <div
                      className="task-deadlines-gantt__remainder-tail"
                      style={{ left: `${remainderTailLeftPct}%`, width: `${remainderTailWidthPct}%` }}
                      title={showRemainderTailClosedEarly ? 'Закрыто раньше срока' : 'Остаток до срока'}
                      aria-hidden
                    />
                  ) : null}
                  {(showOverdueTailClosedLate || showOverdueTailOpen) && overdueTailWidthPct > 0 ? (
                    <div
                      className="task-deadlines-gantt__overdue-tail"
                      style={{ left: `${overdueTailLeftPct}%`, width: `${overdueTailWidthPct}%` }}
                      title="Просрочка"
                      aria-hidden
                    />
                  ) : null}
                  {showStatusDesignations && showClosingMarker ? (
                    <div
                      className="task-deadlines-gantt__closing-marker"
                      style={{ left: `${closingMarkerPct}%` }}
                      title={row.expenseClosingDateLabel ?? undefined}
                    />
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {showLegend ? (
        <div className="task-deadlines-gantt__legend" aria-hidden>
          {usePlanBars ? (
            <>
              <span className="task-deadlines-gantt__legend-item">
                <span className="task-deadlines-gantt__legend-swatch task-deadlines-gantt__legend-swatch--plan" />
                план
              </span>
              {rows.some((row) => (row.extraBars?.length ?? 0) > 0) ? (
                <span className="task-deadlines-gantt__legend-item">
                  <span className="task-deadlines-gantt__legend-swatch task-deadlines-gantt__legend-swatch--extension" />
                  продление ДС
                </span>
              ) : null}
              {showStatusDesignations ? (
                <>
                  <span className="task-deadlines-gantt__legend-item">
                    <span className="task-deadlines-gantt__legend-swatch task-deadlines-gantt__legend-swatch--remainder" />
                    остаток до срока
                  </span>
                  <span className="task-deadlines-gantt__legend-item">
                    <span className="task-deadlines-gantt__legend-swatch task-deadlines-gantt__legend-swatch--overdue" />
                    просрочка
                  </span>
                  <span className="task-deadlines-gantt__legend-item">
                    <span className="task-deadlines-gantt__legend-marker" />
                    {closingLegendLabel}
                  </span>
                </>
              ) : null}
              {showTodayMarker ? (
                <span className="task-deadlines-gantt__legend-item">
                  <span className="task-deadlines-gantt__legend-today" />
                  сегодня
                </span>
              ) : null}
            </>
          ) : (
            <>
              <span className="task-deadlines-gantt__legend-item">
                <span className="task-deadlines-gantt__legend-swatch task-deadlines-gantt__legend-swatch--on-time" />
                в срок
              </span>
              <span className="task-deadlines-gantt__legend-item">
                <span className="task-deadlines-gantt__legend-swatch task-deadlines-gantt__legend-swatch--overdue" />
                просрочка
              </span>
              <span className="task-deadlines-gantt__legend-item">
                <span className="task-deadlines-gantt__legend-marker" />
                закрытие
              </span>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default TaskDeadlinesGantt;
